import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelector } from 'react-redux';
import { selectPage3 } from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { missingField } from 'app/constants';
import { RowSection } from '../Common/RowSection';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { ITypeAheadOption } from '@AMIEWEB/Tasks/CustomComponents/ControlledTypeAheadWithAvatar';
import { AdditionalInformation } from '@AMIEWEB/NewOrder/types';
import moment from 'moment';

const useStyles = makeStyles()(theme => ({
  boxInlineHeadLabelTxt: {
    'font-weight': '600',
    'margin-top': '0px',
    fontSize: '16px',
  },
}));

interface OrderDetailsOneProps {
  isNewOrderCreation: boolean;
}

const OrderDetailsOne = (props: OrderDetailsOneProps) => {
  const { isNewOrderCreation } = props;
  const { t } = useTranslation();
  const { classes } = useStyles();

  const page3Data = useSelector(selectPage3);
  const orderDetailsUIView = useSelector(selectOrderDetailsUIView);
  const orderDetails = orderDetailsUIView?.orderDetails;
  let displayData: { shifts: ITypeAheadOption[]; additionalInformation: AdditionalInformation };

  if (isNewOrderCreation) {
    displayData = {
      shifts: page3Data.shifts?.filter(x => !!x),
      additionalInformation: page3Data.additionalInformation,
      expectedHours:
        page3Data?.expectedHours !== null && !isNaN(page3Data?.expectedHours)
          ? `${page3Data?.expectedHours} Hours`
          : missingField,
      orderBillRate:
        page3Data?.orderBillRate !== null && !isNaN(page3Data?.orderBillRate)
          ? `${t('currency.dollar')} ${page3Data?.orderBillRate}`
          : missingField,
      guaranteedHours:
        page3Data?.guaranteedHours !== null && !isNaN(page3Data?.guaranteedHours)
          ? `${page3Data?.guaranteedHours} Hours`
          : missingField,
      shiftStartTime: page3Data?.shiftStartTime
        ? moment(page3Data.shiftStartTime).format('h:mm A')
        : null,
      shiftEndTime: page3Data?.shiftEndTime
        ? moment(page3Data.shiftEndTime).format('h:mm A')
        : null,
    };
  } else {
    displayData = {
      shifts:
        orderDetails?.shifts?.map(x => {
          return {
            object: x,
            label: x.description,
          };
        }) ?? [],
      additionalInformation: orderDetails?.additionalInformation,
      expectedHours:
        orderDetails?.expectedHours !== null && !isNaN(orderDetails?.expectedHours)
          ? `${orderDetails?.expectedHours} Hours`
          : missingField,
      orderBillRate:
        orderDetails?.orderBillRate !== null && !isNaN(orderDetails?.orderBillRate)
          ? `${t('currency.dollar')} ${orderDetails?.orderBillRate}`
          : missingField,
      guaranteedHours:
        orderDetails?.guaranteedHours !== null && !isNaN(orderDetails?.guaranteedHours)
          ? `${orderDetails?.guaranteedHours} Hours`
          : missingField,
      shiftStartTime: orderDetails?.shiftStartTime
        ? moment(orderDetails.shiftStartTime, 'HHmm').format('h:mm A')
        : null,
      shiftEndTime: orderDetails?.shiftEndTime
        ? moment(orderDetails.shiftEndTime, 'HHmm').format('h:mm A')
        : null,
    };
  }

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
      <Grid item direction="column" width="100%">
        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>{t('order.orderReportDetails.paybonusSection.shifts')}</div>
          }
          data={``}
        />
        <RowSection header={t('order.newOrder.orderBillRate')} data={displayData.orderBillRate} />
        <RowSection header={t('order.newOrder.expectedHours')} data={displayData.expectedHours} />
        <RowSection header={t('order.orderIdHover.gww')} data={displayData.guaranteedHours} />
        <RowSection
          header={t('order.orderReportDetails.orderinfoSection.shift')}
          data={
            <>
              {displayData.shifts.length > 0
                ? displayData.shifts.map((data, index) => (
                    <span key={index}>
                      {data.label ?? missingField}
                      {index !== displayData.shifts.length - 1 && ', '}
                    </span>
                  ))
                : missingField}
            </>
          }
        />
        {displayData.shiftStartTime && (
          <RowSection header="Shift Start Time" data={displayData.shiftStartTime} />
        )}
        {displayData.shiftEndTime && (
          <RowSection header="Shift End Time" data={displayData.shiftEndTime} />
        )}

        <Divider style={{ marginBottom: '12px' }} />

        <RowSection
          header={
            <div className={classes.boxInlineHeadLabelTxt}>
              {t('order.newOrderCreation.tabs.additionalInformation')}
            </div>
          }
          data={``}
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.holidays')}
          data={
            !displayData.additionalInformation?.holidays?.isSelected
              ? 'NA'
              : displayData.additionalInformation?.holidays.selectedValue
              ? displayData.additionalInformation?.holidays.selectedValue
              : missingField
          }
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.weekends')}
          data={
            !displayData.additionalInformation?.weekends?.isSelected
              ? 'NA'
              : displayData.additionalInformation?.weekends.selectedValue
              ? displayData.additionalInformation?.weekends.selectedValue
              : missingField
          }
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.onCall')}
          data={
            !displayData.additionalInformation?.onCall?.isSelected
              ? 'NA'
              : displayData.additionalInformation?.onCall.selectedValue
              ? displayData.additionalInformation?.onCall.selectedValue
              : missingField
          }
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.float')}
          data={
            !displayData.additionalInformation?.float?.isSelected
              ? 'NA'
              : displayData.additionalInformation?.float.selectedValue
              ? displayData.additionalInformation?.float.selectedValue
              : missingField
          }
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.allowBlocks')}
          data={
            !displayData.additionalInformation?.allowBlocks?.isSelected
              ? 'NA'
              : displayData.additionalInformation?.allowBlocks.selectedValue
              ? displayData.additionalInformation?.allowBlocks.selectedValue
              : missingField
          }
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.noTimeOffAllowed')}
          data={
            !displayData.additionalInformation?.noTimeOffAllowed?.isSelected
              ? 'NA'
              : displayData.additionalInformation?.noTimeOffAllowed.selectedValue
              ? displayData.additionalInformation?.noTimeOffAllowed.selectedValue
              : missingField
          }
        />
        <RowSection
          header={t('order.newOrderCreation.orderDetails.additionalInformation.scrubColors')}
          data={
            displayData.additionalInformation && displayData.additionalInformation.scrubColors !== ''
              ? displayData.additionalInformation?.scrubColors
              : 'NA'
          }
        />
      </Grid>
    </Box>
  );
};

export default OrderDetailsOne;
